import { useEffect, useState } from 'react';
import { AlertCircle, KeyRound } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { authErrorMessage } from '@/lib/auth';
import { useTwoFactorRegenerate } from '@/lib/two-factor';
import { BackupCodesView } from './backup-codes-view';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TwoFactorRegenerateDialog({ open, onOpenChange }: Props) {
  const regen = useTwoFactorRegenerate();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [newCodes, setNewCodes] = useState<string[]>([]);

  // `regen` mutation identity changes every render; must stay out of deps
  // to avoid reset→re-render→reset infinite loop. Only the open-edge matters.
  useEffect(() => {
    if (!open) {
      setCode('');
      setPassword('');
      setNewCodes([]);
      regen.reset();
    }
  }, [open]);

  const handleSubmit = () => {
    regen.mutate(
      { code: code.replace(/\s+/g, ''), password },
      { onSuccess: (data) => setNewCodes(data.backupCodes) },
    );
  };

  const hasNewCodes = newCodes.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="text-brand-600 h-5 w-5" />
            สร้าง Backup codes ใหม่
          </DialogTitle>
          <DialogDescription>
            {hasNewCodes
              ? 'รหัสชุดใหม่ — รหัสเก่าทั้งหมดถูกยกเลิกแล้ว เก็บรหัสเหล่านี้ไว้แทน'
              : 'รหัสเก่าจะถูกยกเลิกทั้งหมดและแทนที่ด้วยรหัสใหม่ 10 รหัส'}
          </DialogDescription>
        </DialogHeader>

        {!hasNewCodes ? (
          <>
            <div className="space-y-4">
              {regen.isError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{authErrorMessage(regen.error)}</AlertDescription>
                </Alert>
              )}
              <div>
                <Label htmlFor="regen-code" className="mb-1.5 block">
                  รหัส 6 หลัก หรือ Backup code
                </Label>
                <Input
                  id="regen-code"
                  autoComplete="one-time-code"
                  placeholder="123456 หรือ XXXXX-XXXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="regen-password" className="mb-1.5 block">
                  รหัสผ่านบัญชี
                </Label>
                <PasswordInput
                  id="regen-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                ยกเลิก
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!code || !password || regen.isPending}
              >
                {regen.isPending ? 'กำลังสร้าง...' : 'สร้างรหัสใหม่'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <BackupCodesView codes={newCodes} />
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                เข้าใจแล้ว
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
